import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Check, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : "");

const SuggestionRow = ({ s, onToggle }) => (
  <div className={`rounded-xl border border-white/10 p-4 ${s.status === "done" ? "opacity-50" : "bg-white/[0.02]"}`} data-testid={`inbox-suggestion-${s.id}`}>
    <div className="flex items-center gap-2 flex-wrap text-xs text-slate-400 mb-2">
      <Badge className="bg-[#00f0ff]/15 text-[#00f0ff] border-0 font-tab uppercase">{s.category}</Badge>
      {s.is_pro && <Badge className="bg-[#ffab00]/15 text-[#ffab00] border-0 font-tab">PRO</Badge>}
      <span>{s.name || s.email}</span><span className="text-slate-600">· {s.email} · {fmt(s.created_at)}</span>
      <button onClick={() => onToggle(s)} className="ml-auto inline-flex items-center gap-1 text-slate-400 hover:text-[#00f0ff]" data-testid={`inbox-toggle-${s.id}`}>
        {s.status === "done" ? <><RotateCcw className="w-3.5 h-3.5" /> Reopen</> : <><Check className="w-3.5 h-3.5" /> Mark done</>}
      </button>
    </div>
    <p className="text-sm text-slate-200 whitespace-pre-wrap">{s.message}</p>
  </div>
);

const FlaggedRow = ({ f, i }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid={`inbox-flagged-${i}`}>
    <div className="flex items-center gap-3 flex-wrap">
      <span className="font-heading uppercase tracking-tight">{f.title}</span>
      <span className="text-xs text-slate-500">{f.artist || "Unknown artist"}</span>
      <span className="ml-auto text-xs font-tab"><span className="text-[#FF3366]">{f.down} down</span> <span className="text-slate-500">· {f.up} up</span></span>
    </div>
    {f.reasons.length > 0 && (
      <div className="flex flex-wrap gap-2 mt-2">
        {f.reasons.map((r) => (
          <span key={r.reason} className="text-[11px] font-tab rounded-full border border-white/15 px-2 py-0.5 text-slate-300">{r.reason} ×{r.count}</span>
        ))}
      </div>
    )}
  </div>
);

export const AdminInbox = ({ open, onOpenChange }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/song-requests").then((r) => setRequests(r.data)).catch(() => {});
    api.get("/member-suggestions").then((r) => setSuggestions(r.data)).catch(() => toast.error("Could not load suggestions"));
    api.get("/admin/flagged-songs").then((r) => setFlagged(r.data)).catch(() => toast.error("Could not load flagged songs"));
  }, [open]);

  const markReviewed = async (r) => {
    const note = window.prompt(`Mark "${r.title}" as reviewed? Everyone who voted (${r.votes}) will get an email.\n\nOptional note for them:`, "");
    if (note === null) return;
    try {
      const { data } = await api.post(`/song-requests/${r.id}/reviewed`, { note });
      setRequests((l) => l.map((x) => (x.id === r.id ? { ...x, reviewed: true } : x)));
      window.dispatchEvent(new Event("beatflow:requests-changed"));
      toast.success(`Marked reviewed — emailing ${data.notified} voter${data.notified !== 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not mark reviewed");
    }
  };

  const removeRequest = async (r) => {
    if (!window.confirm(`Remove the request for "${r.title}"?`)) return;
    try {
      await api.delete(`/song-requests/${r.id}`);
      setRequests((l) => l.filter((x) => x.id !== r.id));
      window.dispatchEvent(new Event("beatflow:requests-changed"));
    } catch (e) {
      toast.error("Could not remove request");
    }
  };

  const toggle = async (s) => {
    const status = s.status === "done" ? "new" : "done";
    try {
      await api.patch(`/member-suggestions/${s.id}`, { status });
      setSuggestions((list) => list.map((x) => (x.id === s.id ? { ...x, status } : x)));
    } catch (e) {
      toast.error("Could not update suggestion");
    }
  };

  const empty = (t) => <p className="text-sm text-slate-500 py-8 text-center">{t}</p>;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0b0c10] border-white/10 text-slate-100 max-w-3xl" data-testid="admin-inbox-modal">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight">Feedback inbox</DialogTitle>
          <DialogDescription className="text-slate-400">What members are asking for, and which tabs they say are off.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="suggestions">
          <TabsList className="bg-white/5">
            <TabsTrigger value="suggestions" data-testid="inbox-tab-suggestions">Suggestions ({suggestions.filter((s) => s.status !== "done").length})</TabsTrigger>
            <TabsTrigger value="flagged" data-testid="inbox-tab-flagged">Flagged songs ({flagged.length})</TabsTrigger>
            <TabsTrigger value="requests" data-testid="inbox-tab-requests">Song requests ({requests.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="suggestions" className="max-h-[60vh] overflow-y-auto flex flex-col gap-3 pr-1">
            {suggestions.length ? suggestions.map((s) => <SuggestionRow key={s.id} s={s} onToggle={toggle} />) : empty("No suggestions yet.")}
          </TabsContent>
          <TabsContent value="flagged" className="max-h-[60vh] overflow-y-auto flex flex-col gap-3 pr-1">
            {flagged.length ? flagged.map((f, i) => <FlaggedRow key={`${f.title}-${f.artist}`} f={f} i={i} />) : empty("No songs flagged as inaccurate yet.")}
          </TabsContent>
          <TabsContent value="requests" className="max-h-[60vh] overflow-y-auto flex flex-col gap-2 pr-1">
            {requests.length ? requests.map((r, i) => (
              <div key={r.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3" data-testid={`inbox-request-${r.id}`}>
                <span className="font-tab text-slate-500 w-6">#{i + 1}</span>
                <span className="font-heading uppercase tracking-tight truncate">{r.title}</span>
                <span className="text-xs text-slate-500 truncate">{r.artist}</span>
                <span className="ml-auto font-tab text-xs text-[#00f0ff]">{r.votes} vote{r.votes !== 1 ? "s" : ""}</span>
                {r.reviewed ? (
                  <span className="text-[10px] font-tab uppercase tracking-widest text-emerald-400" data-testid={`inbox-request-reviewed-${r.id}`}>Reviewed</span>
                ) : (
                  <button onClick={() => markReviewed(r)} className="text-[10px] font-tab uppercase tracking-widest rounded-full border border-emerald-400/40 text-emerald-400 px-2 py-1 hover:bg-emerald-400/10" title="Mark as reviewed and email everyone who voted" data-testid={`inbox-request-review-${r.id}`}>
                    Mark reviewed
                  </button>
                )}
                <button onClick={() => removeRequest(r)} className="text-slate-500 hover:text-[#FF3366]" title="Remove" data-testid={`inbox-request-delete-${r.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )) : empty("No song requests yet.")}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
